import { Bot, Phone, Clock, ThumbsUp, Hash } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface AgentMetrics {
  totalAgents: number
  callsThisWeek: number
  avgDurationSeconds: number
  positivePercent: number | null
  ratedCalls: number
  totalPhoneNumbers: number
}

interface AgentMetricsHeaderProps {
  metrics: AgentMetrics
  loading?: boolean
  /** Label fragment for the "Calls" card — e.g. "Last 7 days", "Last 30 days", "All time" */
  rangeLabel?: string
}

export default function AgentMetricsHeader({ metrics, loading, rangeLabel = 'This Week' }: AgentMetricsHeaderProps) {
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const getSentimentDisplay = () => {
    if (metrics.ratedCalls < 3 || metrics.positivePercent === null) {
      return { value: 'No data yet', subtitle: 'Needs 3+ calls' }
    }
    if (metrics.ratedCalls < 10) {
      return { value: `${metrics.positivePercent}%`, subtitle: `Based on ${metrics.ratedCalls} calls` }
    }
    return { value: `${metrics.positivePercent}%`, subtitle: null }
  }

  const sentiment = getSentimentDisplay()

  const metricsData = [
    {
      label: 'AI Agents',
      value: metrics.totalAgents.toString(),
      icon: Bot,
      color: 'text-brand-royal',
      bg: 'bg-brand-primary-50'
    },
    {
      label: 'Phone Numbers',
      value: metrics.totalPhoneNumbers.toString(),
      icon: Hash,
      color: 'text-brand-success',
      bg: 'bg-brand-success-light'
    },
    {
      label: `Calls — ${rangeLabel}`,
      value: metrics.callsThisWeek.toString(),
      icon: Phone,
      color: 'text-brand-indigo',
      bg: 'bg-brand-secondary-light'
    },
    {
      label: 'Avg Duration',
      value: formatDuration(metrics.avgDurationSeconds),
      icon: Clock,
      color: 'text-brand-warning-dark',
      bg: 'bg-brand-warning-light'
    },
  ]

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="h-16 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {metricsData.map((metric) => {
        const Icon = metric.icon
        return (
          <Card key={metric.label} className="border-border/60">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-label text-muted-foreground">{metric.label}</p>
                  <p className="text-2xl font-bold mt-1">{metric.value}</p>
                </div>
                <div className={`p-2.5 rounded-lg ${metric.bg} ${metric.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}

      {/* Sentiment card with tiered display */}
      <Card className="border-border/60">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-label text-muted-foreground">Positive Sentiment</p>
              <p className={`font-bold mt-1 ${sentiment.subtitle && metrics.ratedCalls < 3 ? 'text-base text-muted-foreground' : 'text-2xl'}`}>
                {sentiment.value}
              </p>
              {sentiment.subtitle && (
                <p className="text-xs text-muted-foreground mt-0.5">{sentiment.subtitle}</p>
              )}
            </div>
            <div className="p-2.5 rounded-lg bg-brand-success-light text-brand-success-dark">
              <ThumbsUp className="h-4 w-4" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
